<template>
    <CheckoutElementLayout heading-label="Checkout.Address.Heading"
                           :validation-error="validationError"
                           :show-placeholder="!dataLoaded">
        <div class="flex flex-col md:flex-row print:flex-row relative bg-white p-10 md:p-30 lg:p-40">
            <article class="w-full md:w-1/2">
                <AddressCard heading-label="Checkout.Address.InvoiceHeading"
                             :address="invoiceAddress"/>
            </article>
            <article class="w-full mt-40 md:w-1/2 md:mt-0">
                <slot name="deliveryAddress" :address="deliveryAddress">
                    <AddressCard ref="deliveryAddressRef"
                                 heading-label="Checkout.Address.DeliveryHeading"
                                 validate
                                 :address="deliveryAddress"/>
                </slot>
                <button class="inline-flex items-center mt-20 underline hover:no-underline focus:no-underline"
                        type="button"
                        :disabled="!dataLoaded"
                        :class="validationError ? 'text-red-500' : 'theme-text-linkcolor'"
                        @click="showChangeAddressOverlay()">
                    <CIcon name="edit"
                           width="18"
                           height="18"/>
                    <span class="text-15 ml-10">{{ $dictionary.get(validationError ? 'Checkout.Address.SpecifyAddress' : 'Checkout.Address.ChangeAddress') }}</span>
                </button>
            </article>
            <div v-if="basketUpdateInProgress"
                 class="absolute inset-0">
                <SpinnerOverlay show
                                :delay="0"/>
            </div>
        </div>
    </CheckoutElementLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineAsyncComponent, useTemplateRef } from 'vue';
import CheckoutElementLayout from '@/project/app/client-pages/checkout/CheckoutElementLayout.vue';
import { AddressViewObject, DeliveryAddressViewObject } from '@/types/apiServerContract';
import checkoutChangeAddressOverlayService from '@/project/modals/overlays/CheckoutChangeAddressOverlay.service';
import breakpointsState from '@/core/responsive/breakpoints/breakpointsState.observable';
import { MAX_SM } from '@/core/config/breakpointConfig';
import addressService from './address.service';
import { Validatable } from '@/project/app/client-pages/checkout/types';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';

const AddressCard = defineAsyncComponent(() => import('@/project/ui-components/address/AddressCard.vue'));
type AddressCardType = InstanceType<typeof import('@/project/ui-components/address/AddressCard.vue')['default']>;

interface CheckoutAddressOverviewProps {
    invoiceAddress: AddressViewObject | null;
    deliveryAddress?: AddressViewObject | DeliveryAddressViewObject | null;
    deliveryAddresses: DeliveryAddressViewObject[];
    basketUpdateInProgress?: boolean;
    allowedCountryCodes: string[];
    dataLoaded: boolean;
}

const props = defineProps<CheckoutAddressOverviewProps>();

const emit = defineEmits<{
    (e: 'deliveryAddressUpdated', address: AddressViewObject | DeliveryAddressViewObject): void;
}>();

const validationError = ref(false);
const deliveryAddressRef = useTemplateRef<AddressCardType>('deliveryAddressRef');

const side = computed((): 'left' | 'center' => {
    return breakpointsState.isBreakpointActive(MAX_SM) ? 'left' : 'center';
});

async function showChangeAddressOverlay() {
    const address = await checkoutChangeAddressOverlayService.show(
        props.deliveryAddresses, props.deliveryAddress || null, props.allowedCountryCodes, {
            placement: side.value
        });
    if (address) {
        validationError.value = false;
        emit('deliveryAddressUpdated', address);
    }
}

function validate(): Promise<boolean> {
    const valid = !!(props.deliveryAddress && deliveryAddressRef.value?.isValid());
    validationError.value = !valid;
    return Promise.resolve(valid);
}

watch(
    () => props.deliveryAddresses,
    (value: DeliveryAddressViewObject[]) => {
        addressService.setOriginalDeliveryAddresses(value);
    },
    { immediate: true }
);

interface CheckoutAddressOverviewExposed extends Validatable {
    showChangeAddressOverlay: () => Promise<void>;
}

defineExpose({
    validate,
    showChangeAddressOverlay
} satisfies CheckoutAddressOverviewExposed);
</script>
